import React, { useState } from 'react'
import { post } from '@rails/request.js'
import Alert from './shared/Alert'

const ScanDocumentRoot = () => {
  const [capturedImages, setCapturedImages] = useState([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState(null)

  const handleFileUpload = event => {
    const files = Array.from(event.target.files)

    files.forEach(file => {
      const reader = new FileReader()
      reader.onloadend = () => {
        setCapturedImages(prev => [
          ...prev,
          {
            dataUri: reader.result,
            type: file.type,
            name: file.name,
            source: 'upload',
          },
        ])
      }
      reader.readAsDataURL(file)
    })
  }

  const removeImage = indexToRemove => {
    setCapturedImages(prev => prev.filter((_, index) => index !== indexToRemove))
  }

  const getCustomerDocumentId = () => {
    const pathParts = window.location.pathname.split('/')
    const soloSettlesIndex = pathParts.findIndex(part => part === 'solosettles')
    const customerDocumentId =
      soloSettlesIndex >= 0 && pathParts.length > soloSettlesIndex + 1
        ? pathParts[soloSettlesIndex + 1]
        : null

    if (!customerDocumentId) {
      throw new Error('Could not find customer document ID')
    }

    console.log('customerDocumentId', customerDocumentId)
    return customerDocumentId
  }

  const getCsrfToken = () => {
    const tokenElement = document.querySelector('[name="csrf-token"]')
    return tokenElement?.content
  }

  const handleDone = async () => {
    setIsProcessing(true)

    try {
      const customerDocumentId = getCustomerDocumentId()
      const headers = {}
      const csrfToken = getCsrfToken()
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken
      }

      // Determine which method was used
      const hasCameraImages = capturedImages.some(
        item => typeof item === 'object' && item.source === 'camera'
      )
      const hasUploadImages = capturedImages.some(
        item => typeof item === 'object' && item.source === 'upload'
      )

      // Upload all files in parallel
      const uploadPromises = capturedImages.map((item, index) => {
        const dataUri = item.dataUri
        const type = item.type
        const isPdf = type.includes('pdf')
        const blob = dataURItoBlob(dataUri)
        const extension = isPdf ? 'pdf' : 'jpg'
        const file = new File([blob], `file_${index + 1}.${extension}`, { type })

        const formData = new FormData()
        formData.append('file', file)

        return post(`/api_controller/upload_file/${customerDocumentId}`, {
          body: formData,
        }).then(response => {
          if (!response.ok) {
            throw new Error(`Failed to upload file ${index + 1}`)
          }
          return response.json
        })
      })

      // Wait for all uploads to complete
      await Promise.all(uploadPromises)

      // Now call prepopulate with the method flags
      const prepopulateFormData = new FormData()
      if (hasCameraImages) {
        prepopulateFormData.append('camera', 'true')
      }
      if (hasUploadImages) {
        prepopulateFormData.append('upload', 'true')
      }

      const prepopulateResponse = await post(
        `/api_controller/prepopulate_from_document_files/${customerDocumentId}`,
        {
          body: prepopulateFormData,
        }
      )

      if (!prepopulateResponse.ok) {
        throw new Error('Failed to prepopulate from files')
      }

      const responseData = await prepopulateResponse.json
      window.location.href = responseData.resume_url
    } catch (error) {
      console.error('Error processing images:', error)
      setIsProcessing(false)
      setError('An error occurred while processing your documents. Please try again.')
    }
  }

  // Helper function to convert data URI to Blob
  const dataURItoBlob = dataURI => {
    const byteString = atob(dataURI.split(',')[1])
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]
    const ab = new ArrayBuffer(byteString.length)
    const ia = new Uint8Array(ab)

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i)
    }

    return new Blob([ab], { type: mimeString })
  }

  return (
    <div className='col-md-7'>
      <h1>Upload Document</h1>

      {error && (
        <Alert message='error' type='danger' allowHtml={true}>
          <span dangerouslySetInnerHTML={{ __html: error }} />
        </Alert>
      )}

      <p>
        Please provide the documents you recently received about your debt, one page at a time. Then
        click "Done".
      </p>

      <input
        type='file'
        accept='image/*,application/pdf,.pdf'
        multiple
        onChange={handleFileUpload}
        disabled={isProcessing}
        style={{
          maxWidth: '300px',
        }}
      />

      {capturedImages.length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: '10px',
            flexWrap: 'wrap',
            justifyContent: 'center',
            maxWidth: '500px',
            marginTop: '20px',
          }}
        >
          {capturedImages.map((item, index) => {
            const dataUri = item.dataUri
            const type = item.type
            const isPdf = type.includes('pdf')

            return (
              <div
                key={index}
                style={{
                  width: '100px',
                  height: '140px',
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  position: 'relative',
                }}
              >
                {isPdf ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '10px',
                    }}
                  >
                    <div
                      style={{
                        width: '60px',
                        height: '80px',
                        border: '2px solid #e0e0e0',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        color: '#e74c3c',
                      }}
                    >
                      PDF
                    </div>
                    <div
                      style={{
                        fontSize: '10px',
                        marginTop: '5px',
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        width: '90px',
                      }}
                    >
                      File {index + 1}
                    </div>
                  </div>
                ) : (
                  <img
                    src={dataUri}
                    alt={`Uploaded file ${index + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                )}
                <button
                  onClick={() => removeImage(index)}
                  disabled={isProcessing}
                  style={{
                    position: 'absolute',
                    top: '2px',
                    right: '2px',
                    width: '22px',
                    height: '22px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255, 0, 0, 0.7)',
                    color: 'white',
                    border: 'none',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontSize: '26px',
                    fontWeight: 'bold',
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                    opacity: isProcessing ? 0.5 : 1,
                    padding: 0,
                  }}
                >
                  ×
                </button>
              </div>
            )
          })}
        </div>
      )}

      <div style={{ marginTop: '20px' }}>
        <button
          onClick={handleDone}
          disabled={capturedImages.length === 0 || isProcessing}
          className='btn btn-primary'
        >
          {isProcessing ? (
            <>
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #fff',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  marginRight: '8px',
                  display: 'inline-block',
                  verticalAlign: 'middle',
                }}
              />
              <span style={{ verticalAlign: 'middle' }}>Processing...</span>
            </>
          ) : (
            `Done (${capturedImages.length})`
          )}
        </button>
      </div>

      {/* Add keyframe animation for spinner */}
      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  )
}

export default ScanDocumentRoot
